import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AppSearchBar from '../../../Components/AppSearchBar';
import routes from '../../../Utils/routes';

interface LocationState {
    businesses: any[];
    title: string;
}

const MyBusinesses: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { businesses, title } = location.state as LocationState;

    const [searchInput, setSearchInput] = useState('');
    const [filteredBusinesses, setFilteredBusinesses] = useState(businesses);

    useEffect(() => {
        const filtered = businesses.filter(business =>
            business.Name.toLowerCase().includes(searchInput.trim().toLowerCase())
        );
        setFilteredBusinesses(filtered);
    }, [businesses, searchInput]);

    const handleBack = () => {
        navigate(routes.businessdirectory);
    };

    const handleBusinessClick = (businessId: string) => {
        const selectedBusiness = businesses.find(business => business.Id === businessId);
        if (selectedBusiness) {
            navigate(`/business directory`, {
                state: {
                    businessName: selectedBusiness.Name,
                    businessSource: 'my',
                    selectedBusinessData: selectedBusiness,
                    selectedBusinessId: businessId,
                    view: 'detail'
                }
            });
        }
    };

    const getStatusStyle = (status: string): { bgcolor: string; color: string } => {
        switch (status) {
            case "Approved":
                return { bgcolor: "#D5ECD2", color: "#1F4B2E" };
            case "Pending":
                return { bgcolor: "#FFF4D6", color: "#7A6000" };
            case "Rejected":
                return { bgcolor: "#FCD8D6", color: "#8B2D2B" };
            case "Inactive":
                return { bgcolor: "#E4E4E7", color: "#3F3F46" };
            default:
                return { bgcolor: "#E4E4E7", color: "#3F3F46" };
        }
    };

    return (
        <Box sx={{ 
            p: { xs: 1, sm: 2 },
            height: { sm: 'auto' }
        }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{
                                color: "#373A45",
                                fontWeight: 500,
                                mr: 2
                            }}
                        >
                            {title}
                        </Button>
                    </Box>

                    <Grid container spacing={2} alignItems="center" mb={3}>
                        <Grid item xs={12}>
                            <AppSearchBar
                                localSearchInput={searchInput}
                                handleChange={(e) => setSearchInput(e.target.value)}
                                handleMicHold={() => {}}
                                handleMicRelease={() => {}}
                                openSnackbar={false}
                            />
                        </Grid>
                    </Grid>

                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: { xs: 1, sm: 2 },
                            borderRadius: { xs: '9px', sm: '8px' },
                            backgroundColor: 'white',
                            height: { sm: 'auto' }
                        }}
                    >
                        <Grid container spacing={2}>
                            {filteredBusinesses.length > 0 ? (
                                filteredBusinesses.map((business, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: { xs: 1, sm: 2 },
                                                borderRadius: 2,
                                                backgroundColor: '#F8F9FC',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: '#F0F2F5'
                                                }
                                            }}
                                            onClick={() => handleBusinessClick(business.Id)}
                                        >
                                            <Grid container alignItems="center" spacing={{ xs: 1, sm: 2 }}>
                                                <Grid item xs={2} sm={1}>
                                                    <Box
                                                        component="img"
                                                        src={business.Logo_URL__c || "https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg"}
                                                        alt="logo"
                                                        sx={{
                                                            width: { xs: 36, sm: 48 },
                                                            height: { xs: 36, sm: 48 },
                                                            objectFit: 'cover',
                                                            borderRadius: '50%',
                                                            bgcolor: '#fff'
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={7} sm={7}>
                                                    <Typography 
                                                        fontSize={{ xs: "13px", sm: "16px" }}
                                                        fontWeight={500}
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {business.Name}
                                                    </Typography>
                                                    <Typography 
                                                        fontSize={{ xs: "11px", sm: "14px" }}
                                                        color="text.secondary" 
                                                        mt={0.5}
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <LocationOnOutlinedIcon sx={{ fontSize: { xs: "16px", sm: "18px" }, verticalAlign: 'middle', mr: 0.5 }} />
                                                        {business.Business_Address__c || "No Location"}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        sx={{ 
                                                            mt: { xs: 0.5, sm: 1 },
                                                            borderRadius: '20px',
                                                            textTransform: 'none',
                                                            color: "#373A45",
                                                            border: "1px solid #D9DBE1",
                                                            fontSize: { xs: '11px', sm: '14px' },
                                                            py: { xs: 0, sm: 0.5 },
                                                            px: { xs: 1, sm: 1.5 }
                                                        }}
                                                    >
                                                        {business.Business_Type__c || "No Category"}
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={3} sm={4} sx={{ textAlign: { xs: 'right', sm: 'right' } }}>
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            ...getStatusStyle(business.Business_Approval__c || ""),
                                                            px: { xs: 1, sm: 1.5 },
                                                            py: 0.5,
                                                            borderRadius: '20px',
                                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                            fontWeight: 500,
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        {business.Business_Approval__c || "Pending"}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Typography 
                                        textAlign="center" 
                                        color="text.secondary" 
                                        mt={2}
                                        sx={{ fontSize: { xs: '13px', sm: '14px' } }}
                                    >
                                        No businesses found
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MyBusinesses; 